/**
 * `PublicCvRecap` — pagina pubblica di recap CV, resa FUORI da `Layout` e senza
 * autenticazione (Req. 6.1). Rotta `/public/submission/:token`.
 *
 * Comportamento (Req. 3.2, 3.3, 3.4):
 *  - legge il `:token` opaco dai parametri di rotta;
 *  - interroga periodicamente `GET /api/public/public-cv/profiles/:token/status`
 *    (polling) finché lo stato AI è `pending`, mostrando un'animazione di
 *    caricamento (Req. 3.2);
 *  - quando lo stato diventa `completed` interrompe il polling, scarica la
 *    dashboard read-only (`GET .../dashboard`) e la rende tramite
 *    `PublicCvDashboard` (Req. 3.3);
 *  - quando lo stato diventa `failed` interrompe il polling e mostra uno stato
 *    di errore che spiega il mancato completamento della pre-compilazione AI
 *    (Req. 3.4).
 *
 * Client API token-less (`usePublicApiClient`): nessun Bearer token, nessun
 * re-login OIDC. Gli errori restano categorizzati via `ApiRequestError.kind`:
 *  - `not_found` (404): link non valido o scaduto;
 *  - `conflict` (409): dashboard non ancora pronta durante la corsa tra il
 *    passaggio a `completed` e la disponibilità dell'aggregato → transitorio,
 *    trattato come "ancora in preparazione".
 */
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { usePublicApiClient } from '../../api/usePublicApiClient';
import { ApiRequestError } from '../../api/client';
import type {
  PublicCvStatusDto,
  PublicExternalCvDashboardDto,
} from '../../shared/dto';
import { Alert, Loading, PageHeader } from '../../components/ui';
import PublicCvDashboard from './PublicCvDashboard';

/** Intervallo di polling dello stato AI mentre la pre-compilazione è `pending`. */
const STATUS_POLL_INTERVAL_MS = 3000;

/** Type guard: vero se l'errore è un `ApiRequestError` con la `kind` indicata. */
function isApiErrorKind(
  error: unknown,
  kind: ApiRequestError['kind'],
): error is ApiRequestError {
  return error instanceof ApiRequestError && error.kind === kind;
}

/**
 * Schermata di caricamento generica: la pagina dispone del solo token, quindi
 * non può mostrare un recap dei dati inviati. Un messaggio "stiamo analizzando
 * il tuo CV" con animazione è sufficiente (Req. 3.2).
 */
function AnalyzingState() {
  return (
    <div className="public-cv-recap public-cv-recap--pending">
      <PageHeader
        title="Stiamo analizzando il tuo CV"
        subtitle="L'AI sta pre-compilando il tuo profilo. L'operazione può richiedere qualche istante."
      />
      <Loading>Analisi del CV in corso…</Loading>
    </div>
  );
}

export default function PublicCvRecap() {
  const api = usePublicApiClient();
  const { token } = useParams<{ token: string }>();
  const hasToken = token !== undefined && token !== '';

  // Polling dello stato AI: continua finché `pending`, si ferma su
  // `completed`/`failed`. Un 404 (link non valido/scaduto) non va ritentato.
  const statusQuery = useQuery<PublicCvStatusDto, ApiRequestError>({
    queryKey: ['publicCv', 'status', token],
    queryFn: () =>
      api.get<PublicCvStatusDto>(
        `/api/public/public-cv/profiles/${encodeURIComponent(token ?? '')}/status`,
      ),
    enabled: hasToken,
    retry: false,
    // Il polling si arresta (refetchInterval → false) appena lo stato non è più
    // `pending`, oppure se la query è in errore (es. 404).
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return query.state.data?.aiStatus === 'pending' ? STATUS_POLL_INTERVAL_MS : false;
    },
  });

  const aiStatus = statusQuery.data?.aiStatus;
  const isCompleted = aiStatus === 'completed';

  // Dashboard read-only: scaricata solo quando lo stato è `completed`. Un 409
  // (dashboard non ancora pronta durante la corsa) è transitorio e viene
  // ritentato; un 404 (link non valido/scaduto) non va ritentato.
  const dashboardQuery = useQuery<PublicExternalCvDashboardDto, ApiRequestError>({
    queryKey: ['publicCv', 'dashboard', token],
    queryFn: () =>
      api.get<PublicExternalCvDashboardDto>(
        `/api/public/public-cv/profiles/${encodeURIComponent(token ?? '')}/dashboard`,
      ),
    enabled: hasToken && isCompleted,
    // Ritenta solo sul conflitto transitorio (409); qualsiasi altro errore
    // (es. 404) non va ripetuto.
    retry: (_failureCount, error) => isApiErrorKind(error, 'conflict'),
    retryDelay: STATUS_POLL_INTERVAL_MS,
  });

  // Token assente in rotta: link malformato.
  if (!hasToken) {
    return (
      <div className="public-cv-recap public-cv-recap--error">
        <PageHeader title="Link non valido" />
        <Alert tone="error">
          Il link al recap del CV non è valido. Verifica di aver aperto il collegamento
          completo.
        </Alert>
      </div>
    );
  }

  // Stato AI non trovato: 404 → link non valido o scaduto.
  if (isApiErrorKind(statusQuery.error, 'not_found')) {
    return (
      <div className="public-cv-recap public-cv-recap--error">
        <PageHeader title="Link non valido o scaduto" />
        <Alert tone="error">
          Non abbiamo trovato alcun profilo per questo link: potrebbe essere errato o
          non più valido.
        </Alert>
      </div>
    );
  }

  // Altro errore nel recupero dello stato: mostra un errore generico.
  if (statusQuery.isError) {
    return (
      <div className="public-cv-recap public-cv-recap--error">
        <PageHeader title="Errore" />
        <Alert tone="error">
          Si è verificato un problema nel recupero dello stato del tuo CV. Riprova più
          tardi.
        </Alert>
      </div>
    );
  }

  // Pre-compilazione AI non riuscita (Req. 3.4).
  if (aiStatus === 'failed') {
    return (
      <div className="public-cv-recap public-cv-recap--error">
        <PageHeader title="Pre-compilazione non riuscita" />
        <Alert tone="error">
          La pre-compilazione automatica del tuo profilo non è andata a buon fine. Il
          CV non ha potuto essere analizzato: riprova più tardi o carica nuovamente il
          documento.
        </Alert>
      </div>
    );
  }

  // Stato `completed`: gestione della fetch della dashboard.
  if (isCompleted) {
    // 404 sul dashboard: link non valido o scaduto.
    if (isApiErrorKind(dashboardQuery.error, 'not_found')) {
      return (
        <div className="public-cv-recap public-cv-recap--error">
          <PageHeader title="Link non valido o scaduto" />
          <Alert tone="error">
            Non abbiamo trovato alcun profilo per questo link: potrebbe essere errato o
            non più valido.
          </Alert>
        </div>
      );
    }

    // 409 (transitorio) o dashboard ancora in caricamento: continua a mostrare
    // il caricamento (la query ritenta il conflitto in autonomia).
    if (
      dashboardQuery.data === undefined &&
      (dashboardQuery.isPending ||
        dashboardQuery.isFetching ||
        isApiErrorKind(dashboardQuery.error, 'conflict'))
    ) {
      return <AnalyzingState />;
    }

    // Altro errore nel recupero della dashboard.
    if (dashboardQuery.isError) {
      return (
        <div className="public-cv-recap public-cv-recap--error">
          <PageHeader title="Errore" />
          <Alert tone="error">
            Si è verificato un problema nel caricamento del tuo profilo. Riprova più
            tardi.
          </Alert>
        </div>
      );
    }

    // Dashboard pronta: rendering della vista read-only (Req. 3.3).
    if (dashboardQuery.data !== undefined) {
      return (
        <div className="public-cv-recap public-cv-recap--completed">
          <PublicCvDashboard data={dashboardQuery.data} token={token ?? ''} />
        </div>
      );
    }
  }

  // Stato `pending` (o iniziale, in attesa del primo status): animazione di
  // caricamento e polling attivo (Req. 3.2).
  return <AnalyzingState />;
}
